import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import Select from "react-select";
import axios from "axios";
import DefaultAvatar from "./../../../Assets/img/avatar-default.jpeg";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import LinearProgress from "@mui/material/LinearProgress";

const userGenter = [
    { value: 1, label: "Male" },
    { value: 2, label: "Female" },
];

const userTypeOption = [
    // { value: 1, label: "Admin" },
    // { value: 2, label: "Editor" },
    { value: 3, label: "Member" },
];

const userStatus = [
    { value: 1, label: "Active" },
    { value: 0, label: "In-active" },
];

const EditUserPage = () => {
    const { id } = useParams();
    const history = useNavigate();
    const [user, setUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [currentStep, setCurrentStep] = useState(1);

    const [showPassword, setShowPassword] = useState(false);

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await axios.post(BASE_URL + "API/apicall", {
                    security_key: security_key,
                    pk_user_key: id,
                    mode: "getUserData",
                });
                const output = response.data;

                if (output) {
                    setUser(output);
                }
            } catch (error) {
                toast.error("Error fetching user details.", { autoClose: 3000 });
            }
        };

        fetchUser();
    }, [id]);

    const initialValues = {
        pk_user_key: user ? user.pk_user_key : "",
        pk_ud_key: user ? user.pk_ud_key : "",
        user_name: user ? user.user_name : "",
        first_name: user ? user.first_name : "",
        last_name: user ? user.last_name : "",
        gender: user ? user.gender : "",
        dob: user ? user.dob : "",
        user_email: user ? user.user_email : "",
        password: user ? user.direct_password : "",
        confirm_password: user ? user.direct_password : "",
        user_type: user ? user.user_type : "",
        reg_date: user ? user.reg_date : "",
        website: user ? user.website : "",
        user_img: null,
        profile_mod: user && user.profile_mod ? IMAGE_URL + user.profile_mod : DefaultAvatar,
        phone: user ? user.phone : "",
        bio: user ? user.bio : "",
        address_line1: user ? user.address_line1 : "",
        address_line2: user ? user.address_line2 : "",
        country: user ? user.country : "",
        state: user ? user.state : "",
        city: user ? user.city : "",
        zip_code: user ? user.zip_code : "",
        google: user ? user.google : "",
        facebook: user ? user.facebook : "",
        twitter: user ? user.twitter : "",
        instagram: user ? user.instagram : "",
        pinterest: user ? user.pinterest : "",
        linkedin: user ? user.linkedin : "",
        system_fields: user && user.system_fields ? user.system_fields.split(", ") : [],
        featured: user ? user.featured : "",
        user_status: user ? user.user_status : "",
        updated_by: sessionStorage.getItem("pk_user_key"),
    };

    const validationSchemas = {
        step1: Yup.object().shape({
            user_name: Yup.string()
                .required("Username is required.")
                .min(3, "Username must be at least 3 characters.")
                .max(20, "Username cannot be longer than 20 characters.")
                .matches(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores.")
                .test("unique-link", "Username is already existing in the database", async function (value) {
                    if (!value) return true;
                    const isUserNameUnique = await checkUserName(value);
                    return isUserNameUnique;
                }),

            first_name: Yup.string()
                .min(3, "First Name must be at least 3 characters.")
                .max(50, "First Name must be at most 50 characters.")
                // .matches(/^[a-zA-Z0-9_]*$/, "First Name can only contain letters, numbers, and underscores.")
                .matches(/^[a-zA-Z]*$/, "First Name can only contain letters.")
                .required("First Name is required."),

            last_name: Yup.string()
                .min(3, "Last Name must be at least 3 characters.")
                .max(250, "Last Name must be at most 250 characters.")
                // .matches(/^[a-zA-Z\s]*$/, "Last Name can only contain letters and spaces.")
                .matches(/^[a-zA-Z]*$/, "Last Name can only contain letters.")
                .required("Last Name is required."),

            user_email: Yup.string()
                .email("Invalid email address.")
                .required("email is required.")
                .test("unique-link", "User Email is already existing in the database", async function (value) {
                    if (!value) return true;
                    const isUserEmailUnique = await checkUserEmail(value);
                    return isUserEmailUnique;
                }),

            password: Yup.string()
                .min(8, "Password must be at least 8 characters.")
                .max(100, "Password must be at most 100 characters.")
                .matches(/[a-zA-Z]/, "Password must contain at least one letter.")
                .matches(/\d/, "Password must contain at least one number.")
                .matches(/[@$!%*?&#]/, "Password must contain at least one special character.")
                .required("Password is required."),

            confirm_password: Yup.string()
                .oneOf([Yup.ref("password"), null], "Passwords must match.")
                .required("Confirm password is required."),
        }),

        step2: Yup.object().shape({
            phone: Yup.string()
                .required("Phone number is required.")
                .min(11, "Phone number must be at least 10 digits.")
                .max(16, "Phone number must not exceed 15 digits.")
                .test("unique-digits", "Invalid phone number.", function (value) {
                    const cleanedDigits = value.replace(/^\+\D*/, "");
                    const digitsWithoutCountryCode = cleanedDigits.slice(1);
                    const hasDifferentDigit = Array.from(new Set(digitsWithoutCountryCode)).length !== 1;
                    return hasDifferentDigit || this.createError({ message: "Invalid phone number." });
                }),
        }),

        step3: Yup.object().shape({
            user_status: Yup.string().required("User Status is required."),
        }),
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchemas[`step${currentStep}`],
        enableReinitialize: true,
        onSubmit: async (formValues) => {
            toast.dismiss();
            setIsSubmitting(true);

            const response = await axios.post(
                `${BASE_URL}API/apicall`,
                {
                    security_key,
                    mode: "updateUserData",
                    ...formValues,
                },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const output = response.data;
            if (output == 1) {
                toast.success("Updated successfully!");
                setTimeout(() => history("/admin/user-management"), 3000);
            } else {
                toast.error("Oops! Something went wrong.", {
                    autoClose: 3000,
                });
            }
            setTimeout(function () {
                setIsSubmitting(false);
            }, 3000);
        },
    });

    // Now, you can set variables as needed:
    const values = formik.values;
    const handleBlur = formik.handleBlur;
    const touched = formik.touched;
    const handleChange = formik.handleChange;
    const errors = formik.errors;
    const handleSubmit = formik.handleSubmit;
    const setFieldValue = formik.setFieldValue;
    const setTouched = formik.setTouched;
    const resetForm = formik.resetForm;
    const setValues = formik.setValues;
    const handleReset = formik.handleReset;

    const handleNextStep = async () => {
        try {
            const errors = await formik.validateForm();
            console.log("All Errors:", errors);

            const currentStepFields = Object.keys(validationSchemas[`step${currentStep}`].fields);

            const currentStepErrors = currentStepFields.reduce((acc, field) => {
                if (errors && errors[field]) {
                    acc[field] = errors[field];
                }
                return acc;
            }, {});

            console.log("Current Step Errors:", currentStepErrors);

            if (Object.keys(currentStepErrors).length === 0) {
                setCurrentStep((prevStep) => prevStep + 1);
                formik.setTouched({});
            } else {
                formik.setTouched(
                    currentStepFields.reduce((acc, field) => {
                        acc[field] = true;
                        return acc;
                    }, {})
                );
            }
        } catch (error) {
            console.error("Error in handleNextStep:", error);
        }
    };

    const handlePrevStep = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            setFieldValue("user_img", file);

            const reader = new FileReader();
            reader.onload = () => {
                setFieldValue("profile_mod", reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    async function checkUserName(user_name) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkUserName",
            pk_user_key: id,
            user_name: user_name,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    async function checkUserEmail(user_email) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkUserEmail",
            pk_user_key: id,
            user_email: user_email,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    const today = new Date().toISOString().split("T")[0];

    return (
        <div className="container mt-3 bg-white p-3 mb-3">
            <ToastContainer position="top-center" />
            <h1 className="mb-4 fs-3">Edit User</h1>
            <form onSubmit={handleSubmit} ref={formRef}>
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                    <div>
                        <div className="mb-3">
                            <label className="form-label">
                                User Name <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                name="user_name"
                                // autocomplete="new-password"
                                className="form-control"
                                value={values.user_name}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                            {errors.user_name && touched.user_name && <p className="text-danger">{errors.user_name}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                First Name <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                name="first_name"
                                className="form-control"
                                value={values.first_name}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                            {errors.first_name && touched.first_name && <p className="text-danger">{errors.first_name}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Last name <span className="text-danger">*</span>
                            </label>
                            <input type="text" name="last_name" className="form-control" value={values.last_name} onChange={handleChange} onBlur={handleBlur} />
                            {errors.last_name && touched.last_name && <p className="text-danger">{errors.last_name}</p>}
                        </div>
                        {/* <div className="mb-3">
                            <label className="form-label">Gender</label>
                          

                            <Select
                                options={userGenter}
                                id="gender"
                                name="gender"
                                value={userGenter.filter((option) => values.gender === option.value)}
                                onChange={(selectedOption) => {
                                    const event = {
                                        target: {
                                            name: "gender",
                                            value: selectedOption["value"],
                                        },
                                    };
                                    handleChange(event);
                                }}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    setTouched({ ...touched, gender: true });
                                }}
                            />
                            {touched.gender && errors.gender && <div className="text-danger">{errors.gender}</div>}
                        </div> */}
                        <div className="mb-3">
                            <label className="form-label">
                                Email <span className="text-danger">*</span>
                            </label>
                            <input
                                type="user_email"
                                name="user_email"
                                className="form-control"
                                value={values.user_email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.user_email && touched.user_email && <p className="text-danger">{errors.user_email}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Password <span className="text-danger">*</span>
                            </label>
                            {/* <input
                                type="password"
                                name="password"
                                className="form-control"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            /> */}
                            <div style={{ position: "relative" }}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    name="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter password"
                                    autocomplete="new-password"
                                />
                                <span style={{ position: "absolute", top: "10px", right: "10px" }}>
                                    <a role="button" onClick={toggleShowPassword}>
                                        {showPassword ? <i class="bi bi-eye-slash-fill"></i> : <i class="bi bi-eye-fill"></i>}
                                    </a>
                                </span>
                            </div>
                            {errors.password && touched.password && <p className="text-danger">{errors.password}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Password Confirmation <span className="text-danger">*</span>
                            </label>
                            <input
                                type="password"
                                name="confirm_password"
                                className="form-control"
                                value={values.confirm_password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.confirm_password && touched.confirm_password && <p className="text-danger">{errors.confirm_password}</p>}
                        </div>
                        {/* <div className="mb-3">
                            <label className="form-label">Date of birth</label>
                            <input type="date" name="dob" className="form-control" value={values.dob} onChange={handleChange} onBlur={handleBlur} />
                            {errors.reg_date && touched.reg_date && <p className="text-danger">{errors.reg_date}</p>}
                        </div> */}
                        <div className="mb-3">
                            <label className="form-label">
                                User Group <span className="text-danger">*</span>
                            </label>
                            <Select
                                options={userTypeOption}
                                id="user_type"
                                name="user_type"
                                value={userTypeOption.filter((option) => values.user_type == option.value)}
                                onChange={(selectedOption) => {
                                    const event = {
                                        target: {
                                            name: "user_type",
                                            value: selectedOption["value"],
                                        },
                                    };
                                    handleChange(event);
                                }}
                                onBlur={(event) => {
                                    handleBlur(event);
                                    setTouched({ ...touched, user_type: true });
                                }}
                            />
                            {errors.user_type && touched.user_type && <p className="text-danger">{errors.user_type}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Registered Date <span className="text-danger">*</span>
                            </label>
                            <input
                                type="date"
                                name="reg_date"
                                
                                className="form-control"
                                value={values.reg_date}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                max={today}
                            />
                            {/* {errors.reg_date && touched.reg_date && <p className="text-danger">{errors.reg_date}</p>} */}
                        </div>
                        <Button variant="dark" onClick={handleNextStep}>
                            Next
                        </Button>
                    </div>
                )}

                {/* Step 2: Other Details */}
                {currentStep === 2 && (
                    <div>
                        <div>
                            <div className="position-relative" style={{ cursor: "pointer" }}>
                                <img src={values.profile_mod} className="rounded mx-auto d-block mb-3" width={200} alt="Profile" />
                                <label
                                    className="position-absolute"
                                    style={{ top: "0px", right: "0px", cursor: "pointer" }}
                                    onClick={() => document.getElementById("fileInput").click()}
                                >
                                    <i className="bi bi-pencil-square"></i>
                                </label>
                            </div>

                            <input type="file" onChange={handleImageChange} onBlur={handleBlur} accept="image/*" style={{ display: "none" }} id="fileInput" />
                        </div>
                        {/* <div className="mb-3">
                            <label className="form-label">Address Line 1</label>
                            <input
                                type="text"
                                name="address_line1"
                                className="form-control"
                                value={values.address_line1}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.website && touched.website && <p className="text-danger">{errors.website}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Address Line 2</label>
                            <input
                                type="url"
                                name="address_line2"
                                className="form-control"
                                value={values.address_line2}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.website && touched.website && <p className="text-danger">{errors.website}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Country</label>
                            <input type="text" name="country" className="form-control" value={values.country} onChange={handleChange} onBlur={handleBlur} />
                            {errors.website && touched.website && <p className="text-danger">{errors.website}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">City</label>
                            <input type="url" name="city" className="form-control" value={values.city} onChange={handleChange} onBlur={handleBlur} />
                            {errors.website && touched.website && <p className="text-danger">{errors.website}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Zip Code</label>
                            <input type="url" name="zip_code" className="form-control" value={values.zip_code} onChange={handleChange} onBlur={handleBlur} />
                            {errors.website && touched.website && <p className="text-danger">{errors.website}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">State</label>
                            <input type="url" name="state" className="form-control" value={values.state} onChange={handleChange} onBlur={handleBlur} />
                            {errors.website && touched.website && <p className="text-danger">{errors.website}</p>}
                        </div> */}
                        <div className="mb-3">
                            <label className="form-label">Website</label>
                            <input type="url" name="website" className="form-control" value={values.website} onChange={handleChange} onBlur={handleBlur} />
                            {/* {errors.website && touched.website && <p className="text-danger">{errors.website}</p>} */}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">
                                Phone <span className="text-danger">*</span>
                            </label>
                            <PhoneInput
                                inputStyle={{ width: "100%" }}
                                id="phone"
                                name="phone"
                                countryCodeEditable={false}
                                country="us"
                                value={values.phone}
                                onlyCountries={["us"]}
                                onChange={(value) => {
                                    const event = {
                                        target: {
                                            name: "phone",
                                            value: value,
                                        },
                                    };
                                    handleChange(event);
                                }}
                                onBlur={(e) => {
                                    const event = {
                                        target: {
                                            name: "phone",
                                            value: e.target.value,
                                        },
                                    };
                                    handleBlur(event);
                                    setTouched({ ...touched, phone: true });
                                }}
                            />
                            {errors.phone && touched.phone && <p className="text-danger">{errors.phone}</p>}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">biography</label>
                            <textarea name="bio" className="form-control" value={values.bio} onChange={handleChange} maxLength="500" onBlur={handleBlur} />
                            {errors.bio && touched.bio && <p className="text-danger">{errors.bio}</p>}
                            {/* <small className="form-text text-muted">{500 - formData.bio.length} characters left</small> */}
                        </div>
                        <div className="d-flex justify-content-between">
                            <Button variant="outline-secondary" onClick={handlePrevStep}>
                                Previous
                            </Button>
                            <Button variant="dark" onClick={handleNextStep}>
                                Next
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 3: Social Media */}
                {currentStep === 3 && (
                    <div>
                        <fieldset className="mb-3">
                            <legend>Social</legend>
                            <div className="mb-3">
                                <label className="form-label">Facebook</label>
                                <input
                                    type="url"
                                    name="facebook"
                                    className="form-control"
                                    value={values.facebook}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                {errors.facebook && touched.facebook && <p className="text-danger">{errors.facebook}</p>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Twitter</label>
                                <input type="url" name="twitter" className="form-control" value={values.twitter} onChange={handleChange} onBlur={handleBlur} />
                                {errors.twitter && touched.twitter && <p className="text-danger">{errors.twitter}</p>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Instagram</label>
                                <input
                                    type="url"
                                    name="instagram"
                                    className="form-control"
                                    value={values.instagram}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                {errors.instagram && touched.instagram && <p className="text-danger">{errors.instagram}</p>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Pinterest</label>
                                <input
                                    type="url"
                                    name="pinterest"
                                    className="form-control"
                                    value={values.pinterest}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                {errors.pinterest && touched.pinterest && <p className="text-danger">{errors.pinterest}</p>}
                            </div>
                            {/* <div className="mb-3">
                                <label className="form-label">Google+</label>
                                <input type="url" name="google" className="form-control" value={values.google} onChange={handleChange} onBlur={handleBlur} />
                                {errors.google && touched.google && <p className="text-danger">{errors.google}</p>}
                            </div> */}
                            <div className="mb-3">
                                <label className="form-label">LinkedIn</label>
                                <input
                                    type="url"
                                    name="linkedin"
                                    className="form-control"
                                    value={values.linkedin}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                {errors.linkedin && touched.linkedin && <p className="text-danger">{errors.linkedin}</p>}
                            </div>
                        </fieldset>
                        <fieldset className="mb-3">
                            <legend>System Fields</legend>
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    id="sponsoredCheckbox"
                                    name="system_fields"
                                    value="Sponsored"
                                    className="form-check-input"
                                    checked={values.system_fields.includes("Sponsored")} // Check if "Sponsored" is in the array
                                    onChange={(e) => {
                                        const { checked, value } = e.target;
                                        if (checked) {
                                            setFieldValue("system_fields", [...values.system_fields, value]); // Add to array
                                        } else {
                                            setFieldValue(
                                                "system_fields",
                                                values.system_fields.filter((item) => item !== value) // Remove from array
                                            );
                                        }
                                    }}
                                    onBlur={handleBlur}
                                />
                                <label htmlFor="sponsoredCheckbox" className="form-check-label">
                                    Sponsored
                                </label>
                            </div>

                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    id="FeaturedCheckbox"
                                    name="system_fields"
                                    value="Featured"
                                    className="form-check-input"
                                    checked={values.system_fields.includes("Featured")} // Check if "Featured" is in the array
                                    onChange={(e) => {
                                        const { checked, value } = e.target;
                                        if (checked) {
                                            setFieldValue("system_fields", [...values.system_fields, value]); // Add to array
                                        } else {
                                            setFieldValue(
                                                "system_fields",
                                                values.system_fields.filter((item) => item !== value) // Remove from array
                                            );
                                        }
                                    }}
                                    onBlur={handleBlur}
                                />
                                <label htmlFor="FeaturedCheckbox" className="form-check-label">
                                    Featured
                                </label>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">
                                    Status <span className="text-danger">*</span>
                                </label>
                                <Select
                                    options={userStatus}
                                    id="user_status"
                                    name="user_status"
                                    value={userStatus.filter((option) => values.user_status === option.value)}
                                    onChange={(selectedOption) => {
                                        const event = {
                                            target: {
                                                name: "user_status",
                                                value: selectedOption["value"],
                                            },
                                        };
                                        handleChange(event);
                                    }}
                                    onBlur={(event) => {
                                        handleBlur(event);
                                        setTouched({ ...touched, user_status: true });
                                    }}
                                />
                                {errors.user_status && touched.user_status && <p className="text-danger">{errors.user_status}</p>}
                            </div>
                        </fieldset>
                        <div className="d-flex justify-content-between">
                            <Button variant="outline-secondary" onClick={handlePrevStep}>
                                Previous
                            </Button>
                            <Button variant="dark" type="submit" disabled={isSubmitting}>
                                Save
                                {isSubmitting && <LinearProgress />}
                            </Button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default EditUserPage;
